import { useState, useEffect } from 'react';
import { Search, Filter, X } from 'lucide-react';
import axios from 'axios';
import BlogCard from '../components/BlogCard';
import SEO from '../components/SEO';
import useAnalytics from '../hooks/useAnalytics';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Blog = () => {
  useAnalytics('blog');
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedTag, setSelectedTag] = useState('all');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [postsRes, catsRes, tagsRes] = await Promise.all([
        axios.get(`${API}/posts`),
        axios.get(`${API}/categories`),
        axios.get(`${API}/tags`),
      ]);
      setPosts(postsRes.data);
      setCategories(catsRes.data);
      setTags(tagsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredPosts = posts.filter((post) => {
    const matchesSearch = 
      post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      post.excerpt.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || post.category === selectedCategory;
    const matchesTag = selectedTag === 'all' || post.tags.includes(selectedTag);
    return matchesSearch && matchesCategory && matchesTag;
  });

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedCategory('all');
    setSelectedTag('all');
  };

  const hasActiveFilters = searchTerm || selectedCategory !== 'all' || selectedTag !== 'all';

  return (
    <div className="min-h-screen bg-stone-50 pt-20" data-testid="blog-page">
      <SEO
        title="Stories"
        description="Travel stories and guides from Nepal — trekking, culture, and Himalayan adventures by Marta Scharle."
        url="/blog"
      />
      {/* Header */}
      <section className="py-16 md:py-24 border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          <p className="text-sm tracking-widest uppercase text-red-800 font-medium mb-4">
            The Stories
          </p>
          <h1 className="font-serif text-5xl md:text-6xl text-stone-900 tracking-tight">
            Travel Journal
          </h1>
          <p className="mt-6 text-stone-600 text-lg max-w-2xl">
            Dispatches from the trails, temples, and tea houses of Nepal. 
            Each story is a window into the beauty of the Himalayas.
          </p>
        </div>
      </section>

      {/* Filters */}
      <section className="py-8 border-b border-stone-200 bg-white sticky top-20 z-40">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          <div className="flex flex-col md:flex-row gap-4 items-start md:items-center">
            {/* Search */}
            <div className="relative flex-1 max-w-md">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
              <Input
                type="text"
                placeholder="Search stories..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-stone-50 border-stone-200 focus:border-stone-400 rounded-sm"
                data-testid="blog-search-input"
              />
            </div>

            <div className="flex items-center gap-3">
              <Filter size={18} className="text-stone-400" />
              
              {/* Category Filter */}
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger className="w-[160px] bg-stone-50 border-stone-200 rounded-sm" data-testid="category-filter">
                  <SelectValue placeholder="Category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories.map((cat) => (
                    <SelectItem key={cat.id} value={cat.name}>
                      {cat.name} ({cat.post_count})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Tag Filter */}
              <Select value={selectedTag} onValueChange={setSelectedTag}>
                <SelectTrigger className="w-[140px] bg-stone-50 border-stone-200 rounded-sm" data-testid="tag-filter">
                  <SelectValue placeholder="Tag" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Tags</SelectItem>
                  {tags.map((tag) => (
                    <SelectItem key={tag.id} value={tag.name}>
                      {tag.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="flex items-center gap-1 text-sm text-stone-500 hover:text-stone-900 transition-colors"
                  data-testid="clear-filters-btn"
                >
                  <X size={16} /> Clear
                </button>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Posts Grid */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          {loading ? (
            <div className="flex justify-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-800"></div>
            </div>
          ) : filteredPosts.length > 0 ? (
            <>
              <p className="text-sm text-stone-500 mb-8">
                {filteredPosts.length} {filteredPosts.length === 1 ? 'story' : 'stories'} found
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10">
                {filteredPosts.map((post) => (
                  <BlogCard key={post.id} post={post} />
                ))}
              </div>
            </>
          ) : (
            <div className="text-center py-20" data-testid="no-posts-message">
              <p className="text-stone-500 text-lg mb-4">
                {hasActiveFilters
                  ? 'No stories match your filters.'
                  : 'No stories published yet.'}
              </p>
              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="text-red-800 hover:text-red-900 font-medium"
                >
                  Clear all filters
                </button>
              )}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Blog;
